import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  getToken,
  setAuthToken,
  tokenValid,
  tokenExpiringSoon,
  refreshToken,
  scheduleRefresh,
  clearSession,
  installAuthInterceptor,
} from './auth';

interface AuthContextType {
  isAuthenticated: boolean;
  initializing: boolean;
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    installAuthInterceptor(() => setIsAuthenticated(false));

    const init = async () => {
      if (tokenValid()) {
        setAuthToken(getToken());
        scheduleRefresh();
        setIsAuthenticated(true);
      } else if (await refreshToken()) {
        setIsAuthenticated(true);
      } else {
        clearSession();
        setIsAuthenticated(false);
      }
      setInitializing(false);
    };
    init();

    // refresh when the tab wakes up near/past expiry
    const onWake = async () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      if (getToken() && tokenExpiringSoon()) {
        if (await refreshToken()) {
          setIsAuthenticated(true);
        } else {
          clearSession();
          setIsAuthenticated(false);
        }
      }
    };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    return () => {
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, initializing, setIsAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export default AuthProvider;
